import {createSketchDimensionHistoryPatch} from '../../history/dimension/SketchDimensionHistoryAdapter';
import type {EntityPatch} from '../../history/types';
import {createExistingHistorySnapshot, createMissingHistorySnapshot} from '../../history/types';
import {SketchDimension, resolveSketchDimensionQuantityKind} from './model/SketchDimension';
import {SketchDimensionIdentityService} from './repository/SketchDimensionIdentityService';
import type {SketchDimensionRepository} from './repository/SketchDimensionRepository';
import type {
    SketchDimensionBranchState,
    SketchDimensionKind,
    SketchDimensionReferenceRecord,
    SketchDimensionSnapshot,
} from './SketchDimensionTypes';

/** 一条待创建尺寸的规划描述。 */
export interface PlannedSketchDimension {
    /** 核心类型。 */
    readonly dimensionKind: SketchDimensionKind;
    /** 名义值（核心单位：米/弧度）。 */
    readonly nominalValue: number;
    /** 有序稳定引用。 */
    readonly references: readonly SketchDimensionReferenceRecord[];
    /** 标注位置（草图局部坐标）。 */
    readonly labelPosition: readonly [number, number];
    /** 显示样式（半径/直径式等）。 */
    readonly displayStyle?: 'linear' | 'radial' | 'diametric';
    /** 多解分支；三点角必须显式声明旋向，否则优角会被求解器解成劣角。 */
    readonly branch?: SketchDimensionBranchState;
}

/** 创建输入。 */
export interface CreatePlannedSketchDimensionsInput {
    /** 尺寸仓库。 */
    readonly repository: SketchDimensionRepository;
    /** 草图持久 ID。 */
    readonly sketchId: string;
    /** 规划好的尺寸列表。 */
    readonly planned: readonly PlannedSketchDimension[];
    /** 身份服务（缺省新建）。 */
    readonly identityService?: SketchDimensionIdentityService;
}

/**
 * 按规划批量创建 driving 尺寸（写仓库并返回历史补丁）。
 *
 * 设计说明：
 * 1. **全仓唯一一份**"规划 → 真实尺寸"的落库实现：槽口 Add Dimensions、
 *    圆角/倒角自动尺寸等创建路共用，杜绝各自再拼一遍快照结构；
 * 2. 自动尺寸一律 driving（对齐 SW）；名称按草图内已占用名递增分配；
 * 3. 事务纪律：只写仓库并返回补丁，由调用方与几何创建的运行时快照合成
 *    **同一笔** CadTransaction（HIST-001），撤销一步回到几何与尺寸都不存在。
 *
 * @param input 创建输入。
 * @returns 尺寸历史补丁列表；规划为空时为空数组。
 */
export function createPlannedSketchDimensions(input: CreatePlannedSketchDimensionsInput): EntityPatch[] {
    if (input.planned.length === 0) {
        return [];
    }

    const identity = input.identityService ?? new SketchDimensionIdentityService();
    const occupiedNames = new Set(
        input.repository.listBySketch(input.sketchId).map((dimension) => dimension.currentName),
    );
    const patches: EntityPatch[] = [];

    input.planned.forEach((item) => {
        const dimensionId = identity.allocateDimensionId();
        const quantityKind = resolveSketchDimensionQuantityKind(item.dimensionKind);
        const name = identity.allocateName(occupiedNames);

        occupiedNames.add(name);

        const snapshot: SketchDimensionSnapshot = {
            dimensionId,
            sketchId: input.sketchId,
            name,
            dimensionKind: item.dimensionKind,
            space: 'sketch-2d',
            role: 'driving',
            suppressed: false,
            readOnly: false,
            parameter: {
                parameterId: identity.createParameterId(dimensionId, quantityKind),
                nominalValue: item.nominalValue,
                quantityKind,
                valueScale: 1,
                expression: null,
            },
            measuredValue: null,
            references: item.references,
            branch: item.branch ?? {},
            display: {
                labelPosition: item.labelPosition,
                ...(item.displayStyle ? {displayStyle: item.displayStyle} : {}),
            },
            solveState: 'unknown',
            repair: null,
        };

        input.repository.create(SketchDimension.fromSnapshot(snapshot));
        patches.push(createSketchDimensionHistoryPatch({
            entityId: dimensionId,
            operation: 'create',
            before: createMissingHistorySnapshot(),
            after: createExistingHistorySnapshot(snapshot),
            metadata: {sketchId: input.sketchId},
        }));
    });

    return patches;
}
